import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AcademicYears } from '../../types';
import schoolService from '../../services/schoolService';

const STORAGE_KEY = 'academicYear';

interface AcademicYearState {
  /** Every year on record, largest first. */
  years: number[];
  /** The largest year on record. */
  current: number | null;
  /** The year before the current one, when there is one. */
  previous: number | null;
  /** The year the app is currently scoped to. */
  selected: number | null;
  isLoading: boolean;
  error: string | null;
}

const readStoredYear = (): number | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const initialState: AcademicYearState = {
  years: [],
  current: null,
  previous: null,
  // Restored from a previous session, then reconciled against the server in fetchAcademicYears.
  selected: readStoredYear(),
  isLoading: false,
  error: null,
};

export const fetchAcademicYears = createAsyncThunk(
  'academicYear/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await schoolService.getAcademicYears();
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch academic years');
    }
  }
);

const academicYearSlice = createSlice({
  name: 'academicYear',
  initialState,
  reducers: {
    setSelectedYear: (state, action: PayloadAction<number | null>) => {
      state.selected = action.payload;
      if (action.payload == null) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, String(action.payload));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAcademicYears.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAcademicYears.fulfilled, (state, action: PayloadAction<AcademicYears>) => {
        const { years, current, previous } = action.payload;
        state.isLoading = false;
        state.years = years ?? [];
        state.current = current ?? null;
        state.previous = previous ?? null;

        // Default to the current year, and fall back to it if a stored year has
        // since disappeared from the data.
        if (state.selected == null || !state.years.includes(state.selected)) {
          state.selected = state.current;
          if (state.current == null) {
            localStorage.removeItem(STORAGE_KEY);
          } else {
            localStorage.setItem(STORAGE_KEY, String(state.current));
          }
        }
      })
      .addCase(fetchAcademicYears.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedYear } = academicYearSlice.actions;
export default academicYearSlice.reducer;
